import { computed, reactive, ref } from 'vue'
import { linearTasksApi } from '@/api/linearTasks'
import { createRequestGuard, withAsyncState } from '@/lib/asyncState'
import { describeMergeResult, mergeErrorMessage, type MergeNotice } from '@/lib/pullRequestQueue'
import type {
  LinearTaskBuildBucketFilters,
  LinearTaskBuildBucketItem,
  LinearTaskBuildBuckets,
  PaginationMeta,
} from '@/types/models'

type BucketKey = keyof LinearTaskBuildBuckets

const BUCKET_KEYS: BucketKey[] = ['ready_to_merge', 'fixing', 'in_progress', 'failed']

// Singleton, following useReviewRuns.ts's shape: shared state so the Builds
// view retains its list/pagination across navigation within a session.
//
// Tasks arrive already sorted into their four sections and independently
// paginated (see LinearTaskPullRequestBucketsController) — null until the
// first fetch resolves, rather than an empty-but-populated shape, so a
// section can't briefly read as "genuinely empty" before that first response
// lands.
const buckets = ref<LinearTaskBuildBuckets | null>(null)
const loading = ref(false)
const error = ref<string | null>(null)
// Track background poll failures separately from foreground errors so stale data
// is visible as such, but user actions' errors remain prominent.
const backgroundPollFailed = ref(false)
// Track the last-applied filters so dismissTask can refetch with the same filters
const lastAppliedFilters = ref<LinearTaskBuildBucketFilters>({})

// Guards against a stale filter/page request's response landing after a
// newer one's and clobbering it.
const requestGuard = createRequestGuard()
// Separate guard for foreground requests to track loading state independently
// of background polls.
const foregroundGuard = createRequestGuard()
// Throw-away error ref for background calls so they don't clear user-facing errors.
const backgroundError = ref<string | null>(null)

// Task ids currently mid-merge-request, so the Merge button can disable
// itself per-row rather than globally while one task's request is in flight.
const mergingTaskIds = reactive(new Set<string>())
// The one-line banner shown after a merge attempt resolves — cleared on the
// next attempt or on reset, never auto-dismissed, since a queued-vs-skipped
// split is worth leaving on screen until the user acts again.
const mergeNotice = ref<MergeNotice | null>(null)
// Incremented on reset() so a merge response that lands after a logout (or
// after the caller has otherwise moved on) can't set mergingTaskIds/
// mergeNotice for a session that's no longer current — same pattern as
// useBuildPullRequests' sessionEpoch.
const mergeEpoch = ref(0)

export function useLinearTaskPullRequests() {
  async function fetchLinearTaskPullRequests(
    filters: LinearTaskBuildBucketFilters = {},
    options: { background?: boolean } = {},
  ): Promise<void> {
    const token = requestGuard.start()
    const foregroundToken = options.background ? null : foregroundGuard.start()
    // Background polls reuse whatever filters were last applied in the
    // foreground, so they must not overwrite lastAppliedFilters with their
    // own call's filters.
    if (!options.background) lastAppliedFilters.value = filters
    const success = await withAsyncState(
      options.background ? backgroundError : error,
      'Failed to load Linear pull request history.',
      async () => {
        const result = await linearTasksApi.listPullRequestBuckets(filters)
        if (!requestGuard.isCurrent(token)) return false
        buckets.value = result
        return true
      },
      options.background ? undefined : loading,
      undefined,
      () => (options.background ? !requestGuard.isCurrent(token) : foregroundToken !== null && !foregroundGuard.isCurrent(foregroundToken)),
    )
    // Track background poll failures so users see when data may be stale.
    // A successful foreground fetch (filter change, pagination, mount) means
    // the data on screen is fresh again, so it clears the flag too.
    if (options.background) {
      if (requestGuard.isCurrent(token)) backgroundPollFailed.value = !success
    } else if (success) {
      backgroundPollFailed.value = false
    }
  }

  async function retryTask(id: string, instructions?: string): Promise<void> {
    await withAsyncState(error, 'Failed to retry task.', async () => {
      await linearTasksApi.retry(id, instructions)
      if (!buckets.value) return

      const patch = (task: LinearTaskBuildBucketItem): LinearTaskBuildBucketItem =>
        task.id === id
          ? { ...task, status: 'running', error_message: null, ...(instructions !== undefined ? { description: instructions } : {}) }
          : task

      // The retried task could be sitting in any one of the four buckets —
      // patched wherever it's found rather than looked up first, since which
      // bucket it's currently in isn't tracked separately from its data.
      const patched = { ...buckets.value }
      for (const key of BUCKET_KEYS) {
        patched[key] = { ...patched[key], data: patched[key].data.map(patch) }
      }
      buckets.value = patched
    })
  }

  async function dismissTask(id: string): Promise<void> {
    await withAsyncState(error, 'Failed to dismiss task.', async () => {
      await linearTasksApi.dismiss(id)
      if (!buckets.value) return

      // A dismissed task no longer matches any of
      // BucketLinearTasksForBuildsPageAction's four sections (see its
      // bucketFor) — removed here rather than patched in place, unlike
      // retryTask above which flips the task back to 'running' in whichever
      // bucket it's already sitting in.
      const patched = { ...buckets.value }
      let emptyPageBucket: BucketKey | null = null
      for (const key of BUCKET_KEYS) {
        const bucket = patched[key]
        const data = bucket.data.filter((task) => task.id !== id)
        if (data.length === bucket.data.length) continue
        // last_page is recomputed here too, not just total — PaginationFooter
        // gates its Next button on current_page < last_page, so leaving
        // last_page stale could point Next at a page that no longer exists.
        const newTotal = Math.max(0, bucket.meta.total - 1)
        const newMeta = { ...bucket.meta, total: newTotal, last_page: Math.max(1, Math.ceil(newTotal / bucket.meta.per_page)) }
        patched[key] = { ...bucket, data, meta: newMeta }
        // Only step back a page when the bucket still has items elsewhere —
        // without the total > 0 check, dismissing a bucket's last remaining
        // task would also trigger a pointless refetch.
        if (data.length === 0 && newMeta.total > 0) {
          emptyPageBucket = key
        }
      }
      buckets.value = patched

      // If dismissing a task emptied the current page and there are more items
      // in the bucket, refetch the previous page (same pattern as
      // usePendingRepoRules' resolveRules)
      if (emptyPageBucket !== null) {
        const bucket = patched[emptyPageBucket]
        const targetPage = Math.max(1, bucket.meta.current_page - 1)
        const filters: LinearTaskBuildBucketFilters = { ...lastAppliedFilters.value }
        if (emptyPageBucket === 'ready_to_merge') filters.ready_to_merge_page = targetPage
        else if (emptyPageBucket === 'fixing') filters.fixing_page = targetPage
        else if (emptyPageBucket === 'in_progress') filters.in_progress_page = targetPage
        else if (emptyPageBucket === 'failed') filters.failed_page = targetPage
        await fetchLinearTaskPullRequests(filters)
      }
    })
  }

  // Merges every currently-passing repo PR on a Ready to Merge task. The
  // response is a request-time snapshot (queued vs. skipped), not the merge
  // outcome itself — actual merges land async via a queued job — so this
  // just surfaces that snapshot as a banner and leaves refetching the bucket
  // (to see PRs drop off once merged) to the caller/poll.
  async function mergeTask(id: string): Promise<void> {
    if (mergingTaskIds.has(id)) return
    const epoch = mergeEpoch.value
    mergingTaskIds.add(id)
    mergeNotice.value = null
    try {
      const result = await linearTasksApi.mergePullRequests(id)
      if (epoch === mergeEpoch.value) mergeNotice.value = describeMergeResult(result)
    } catch (mergeError) {
      if (epoch === mergeEpoch.value) mergeNotice.value = { type: 'error', message: mergeErrorMessage(mergeError) }
    } finally {
      if (epoch === mergeEpoch.value) mergingTaskIds.delete(id)
    }
  }

  // Called on logout so the next session's Builds view doesn't briefly show
  // the previous user's data before its own fetch resolves, and so a late
  // response from a request the previous session started can't land and
  // repopulate it.
  function reset(): void {
    requestGuard.start()
    buckets.value = null
    loading.value = false
    error.value = null
    backgroundPollFailed.value = false
    lastAppliedFilters.value = {}
    mergingTaskIds.clear()
    mergeNotice.value = null
    mergeEpoch.value++
  }

  function bucketTasks(key: BucketKey): LinearTaskBuildBucketItem[] {
    return buckets.value?.[key].data ?? []
  }

  function bucketMeta(key: BucketKey): PaginationMeta | null {
    return buckets.value?.[key].meta ?? null
  }

  return {
    loading,
    error,
    backgroundPollFailed,
    mergingTaskIds,
    mergeNotice,
    fetchLinearTaskPullRequests,
    retryTask,
    dismissTask,
    mergeTask,
    reset,
    readyToMergeTasks: computed(() => bucketTasks('ready_to_merge')),
    fixingTasks: computed(() => bucketTasks('fixing')),
    inProgressTasks: computed(() => bucketTasks('in_progress')),
    failedTasks: computed(() => bucketTasks('failed')),
    readyToMergeMeta: computed(() => bucketMeta('ready_to_merge')),
    fixingMeta: computed(() => bucketMeta('fixing')),
    inProgressMeta: computed(() => bucketMeta('in_progress')),
    failedMeta: computed(() => bucketMeta('failed')),
  }
}
